import React, { useEffect, useState } from 'react'
import { useParams, navigate, routes, Redirect } from '@cedarjs/router'
import { useQuery, useMutation } from '@cedarjs/web'
import { gql, useLazyQuery } from '@apollo/client'
import { Metadata } from '@cedarjs/web'
import { toast } from 'react-hot-toast'
import { Alert, Box, CircularProgress, Typography, Link } from '@mui/material'
import { useAuth } from 'src/auth'
import IsolatedFormRenderer from 'src/components/IsolatedFormRenderer/IsolatedFormRenderer'
import FileUploadButton from 'src/components/FileUploadButton'
import useUploadFile from 'src/hooks/requests/useUploadFile'
import Loading from 'src/components/Loading'
import _ from 'lodash'
import {
  CREATE_FORM_SUBMISSION_MUTATION,
  GET_USER_FORM_SUBMISSION_QUERY,
  UPDATE_FORM_SUBMISSION_MUTATION,
} from 'src/pages/SubmitFormPage/queries'
import { FormSubmission } from 'types/graphql'

const GET_FORM_TEMPLATE_QUERY = gql`
  query GetFormTemplateForSubmission(
    $id: String!
    $status: FormTemplateStatus
  ) {
    formTemplate(id: $id, status: $status) {
      id
      title
      description
      formSchema
      status
      visibility
      orgSlug
    }
  }
`

const VALIDATE_PIN_AND_SIGN_FIELD_MUTATION = gql`
  mutation ValidatePinAndSignField($input: ValidatePinAndSignFieldInput!) {
    validatePinAndSignField(input: $input) {
      success
      error
      formSubmission {
        id
        submittedAt
        submissionData
        status
      }
    }
  }
`

const SubmitFormPage = () => {
  const { id } = useParams()
  const { isAuthenticated, loading: isAuthLoading } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [existingSubmissionError, setExistingSubmissionError] = useState<
    string | null
  >(null)
  const [getSubmissionLoading, setGetSubmissionLoading] = useState(false)
  const [currentFormSubmission, setCurrentFormSubmission] =
    useState<FormSubmission | null>(null)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null)
  const {
    data,
    loading: getFormTemplateLoading,
    error,
  } = useQuery(GET_FORM_TEMPLATE_QUERY, {
    variables: { id: id || '', status: 'PUBLISHED' },
    skip: !id,
  })
  const form = data?.formTemplate

  const [createFormSubmission, { loading: createFormSubmissionLoading }] =
    useMutation(CREATE_FORM_SUBMISSION_MUTATION)
  const [getFormSubmission] = useLazyQuery(GET_USER_FORM_SUBMISSION_QUERY)
  const [updateFormSubmission] = useMutation(UPDATE_FORM_SUBMISSION_MUTATION)
  const uploadFile = useUploadFile()

  // Auto-save function
  const performAutoSave = _.debounce(
    async (formData: Record<string, unknown>) => {
      // Only auto-save for authenticated users with org-level forms and existing submissions
      if (
        !isAuthenticated ||
        form?.visibility === 'PUBLIC' ||
        !currentFormSubmission?.id ||
        currentFormSubmission.status !== 'IN_PROGRESS' ||
        isSubmitting
      ) {
        return
      }

      setIsAutoSaving(true)
      setAutoSaveError(null)

      try {
        await updateFormSubmission({
          variables: {
            id: currentFormSubmission.id,
            input: {
              submissionData: formData,
            },
          },
        })
      } catch (error) {
        console.error('Auto-save failed:', error)
        setAutoSaveError('Auto-save failed')
      } finally {
        setIsAutoSaving(false)
      }
    },
    1500
  )

  // Load existing form submission if this is an org form and the user is authenticated.  If not found, create a new submission
  const [validatePinAndSignField] = useMutation(
    VALIDATE_PIN_AND_SIGN_FIELD_MUTATION
  )

  useEffect(() => {
    const loadExistingFormSubmission = async () => {
      try {
        setGetSubmissionLoading(true)
        const { data } = await getFormSubmission({
          variables: { formTemplateId: id || '' },
        })
        if (data?.activeFormSubmissionForUser) {
          setCurrentFormSubmission(data?.activeFormSubmissionForUser)
        } else {
          const { data: newFormSubmission } = await createFormSubmission({
            variables: {
              input: {
                formTemplateId: id,
                submissionData: {},
                submissionType: 'FORM',
              },
            },
          })
          setCurrentFormSubmission(newFormSubmission.createFormSubmission)
        }
        setGetSubmissionLoading(false)
      } catch (error) {
        setExistingSubmissionError('Failed to load existing form submission')
        console.error('Error loading existing form submission:', error)
        toast.error('Failed to load existing form submission')
      } finally {
        setGetSubmissionLoading(false)
      }
    }
    if (
      id &&
      !existingSubmissionError &&
      isAuthenticated &&
      !currentFormSubmission &&
      !isAuthLoading &&
      !getSubmissionLoading &&
      form?.visibility !== 'PUBLIC'
    ) {
      loadExistingFormSubmission()
    }
  }, [
    id,
    form,
    isAuthenticated,
    isAuthLoading,
    getSubmissionLoading,
    existingSubmissionError,
  ])

  const handleSubmit = async (submission: Record<string, unknown>) => {
    setIsSubmitting(true)
    try {
      if (
        currentFormSubmission?.id &&
        currentFormSubmission.status === 'IN_PROGRESS'
      ) {
        // Update existing IN_PROGRESS submission and mark as SUBMITTED
        await updateFormSubmission({
          variables: {
            id: currentFormSubmission.id,
            input: {
              submissionData: submission,
              status: 'SUBMITTED',
              submittedAt: new Date().toISOString(),
            },
          },
        })
      } else {
        // Create new submission for public forms or if no existing IN_PROGRESS submission
        await createFormSubmission({
          variables: {
            input: {
              formTemplateId: id,
              submissionData: submission,
              submissionType: 'FORM',
              status: 'SUBMITTED',
              submittedAt: new Date().toISOString(),
            },
          },
        })
      }

      navigate(routes.submitFormConfirmation({ id }))
    } catch (error) {
      console.error('Error submitting form:', error)
      toast.error('Failed to submit form. Please try again.')
    }
    setIsSubmitting(false)
  }

  const handleValidatePinAndSignField = async (
    submissionData: Record<string, unknown>,
    pin: string,
    fieldKey: string
  ) => {
    setIsSubmitting(true)
    try {
      const result = await validatePinAndSignField({
        variables: {
          input: {
            fieldKey,
            formTemplateId: id,
            submissionData,
            submissionType: 'FORM',
            submissionId: currentFormSubmission?.id,
            pin,
          },
        },
      })

      if (result.data?.validatePinAndSignField?.success) {
        const { formSubmission } = result.data.validatePinAndSignField

        setCurrentFormSubmission(formSubmission)

        toast.success('Field signed successfully!')
      } else {
        console.error(result.data?.validatePinAndSignField?.error)
        toast.error('Failed to sign field')
      }
    } catch (error) {
      console.error('Error signing field:', error)
      toast.error('Failed to sign field. Please try again.')
    }
    setIsSubmitting(false)
  }

  const handlePdfUploadAndSubmit = async (file: File) => {
    setIsSubmitting(true)
    try {
      const fileId = await uploadFile(file)
      await createFormSubmission({
        variables: {
          input: {
            formTemplateId: id,
            status: 'SUBMITTED',
            submittedAt: new Date().toISOString(),
            submissionType: 'PDF',
            submissionFileId: fileId,
            submissionData: {},
          },
        },
      })
      navigate(routes.submitFormConfirmation({ id }))
    } catch (e) {
      console.error('Error uploading PDF and creating submission:', e)
      toast.error('Failed to upload PDF. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Organization form but user not authenticated
  if (form?.visibility !== 'PUBLIC' && !isAuthenticated && !isAuthLoading) {
    return <Redirect to={routes.forbidden()} />
  }

  // Loading state - wait for both form template AND submission data for org forms
  const needsSubmissionData = isAuthenticated && form?.visibility !== 'PUBLIC'
  const isLoadingSubmissionData =
    needsSubmissionData && !currentFormSubmission && !existingSubmissionError
  if (
    getFormTemplateLoading ||
    getSubmissionLoading ||
    isAuthLoading ||
    createFormSubmissionLoading ||
    isLoadingSubmissionData
  ) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircularProgress />
      </div>
    )
  }

  let errorMessage = ''
  if (error) {
    errorMessage = 'Error loading form. Please try again later.'
  } else if (!form) {
    errorMessage = 'Form not found.'
  } else if (!form.formSchema) {
    errorMessage = 'Form not properly configured.'
  }
  if (existingSubmissionError) {
    errorMessage = existingSubmissionError
  }

  if (errorMessage) {
    return (
      <Box className="py-8">
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    )
  }

  // Determine page title
  const getPageTitle = () => {
    if (
      getFormTemplateLoading ||
      getSubmissionLoading ||
      isAuthLoading ||
      createFormSubmissionLoading
    )
      return 'Submit Form'
    if (error) return 'Submit Form'
    if (!form) return 'Form Not Found'
    return `Submit: ${form.title}`
  }

  return (
    <>
      <Metadata title={getPageTitle()} description="Submit Form" />
      <>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center">
            {/* Auto-save status indicator for authenticated org forms */}
            {isAuthenticated &&
              form?.visibility !== 'PUBLIC' &&
              currentFormSubmission?.id && (
                <div className="flex items-center text-sm">
                  {isAutoSaving ? (
                    <div className="flex items-center text-blue-600">
                      <CircularProgress size={12} className="mr-1" />
                      <Typography variant="caption">Auto-saving...</Typography>
                    </div>
                  ) : autoSaveError ? (
                    <div className="flex items-center text-red-600">
                      <Typography variant="caption">{autoSaveError}</Typography>
                    </div>
                  ) : (
                    <div className="flex items-center text-green-600">
                      <Typography variant="caption">
                        Auto-save enabled
                      </Typography>
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="flex items-center">
            <Typography className="mr-1">Have a hard copy?</Typography>
            <FileUploadButton
              accept="application/pdf"
              multiple={false}
              alternativeOnSave={handlePdfUploadAndSubmit}
              button={
                <Link className="cursor-pointer">Upload a completed PDF</Link>
              }
            />
            <Typography className="ml-[2px]">instead</Typography>
          </div>
        </div>
        {isSubmitting ? (
          <Loading message="Submitting..." />
        ) : (
          <IsolatedFormRenderer
            formTemplate={form}
            formSubmission={currentFormSubmission}
            onSubmit={handleSubmit}
            onValidatePinAndSignField={handleValidatePinAndSignField}
            onError={(error) => {
              console.error('Form validation error:', error)
              toast.error('Please fix the form errors before submitting.')
            }}
            onFormChange={performAutoSave}
            options={{
              readOnly: isSubmitting,
              noAlerts: true,
            }}
          />
        )}
      </>
    </>
  )
}

export default SubmitFormPage
